/**
 * Caption PLACEMENT zones — a light, presentation-only gameplay lever.
 *
 * The draggable caption on the video snaps to one of three vertical zones. Each
 * zone has a canonical per-mille anchor (where the caption settles) and a small,
 * DATA-DRIVEN emotion contribution authored in the scenario's `LandingConfig`
 * (`levers.captionPlacement`). This module owns the geometry (which zone a given
 * anchor is nearest, where each zone sits) and the plain-words cue copy; the
 * EFFECT itself always comes from the config, folded into `editEmotionVector`.
 *
 * Presentation only; never feeds `@desk/core` scoring.
 */

import {
  EMOTION_AXES,
  EMOTION_AXIS_COLORS,
  type CaptionPlacementZone,
  type EmotionAxis,
  type LandingConfig,
} from "../../game/emotion-model.js";

/** Canonical per-mille anchor (x, y) where the caption settles in each zone. */
export const PLACEMENT_ZONE_ANCHORS: Record<
  CaptionPlacementZone,
  { xMilli: number; yMilli: number }
> = {
  top: { xMilli: 500, yMilli: 150 },
  subject: { xMilli: 500, yMilli: 500 },
  chyron: { xMilli: 500, yMilli: 880 },
};

/** Zones ordered top -> subject -> chyron by vertical position. */
export const PLACEMENT_ZONE_ORDER: CaptionPlacementZone[] = ["top", "subject", "chyron"];

/** The zone whose canonical anchor is vertically nearest a given y (per-mille). */
export function placementZoneForAnchorY(yMilli: number): CaptionPlacementZone {
  let best: CaptionPlacementZone = "top";
  let bestDist = Infinity;
  for (const zone of PLACEMENT_ZONE_ORDER) {
    const dist = Math.abs(PLACEMENT_ZONE_ANCHORS[zone].yMilli - yMilli);
    if (dist < bestDist) {
      bestDist = dist;
      best = zone;
    }
  }
  return best;
}

/** Short zone name + plain-words effect copy (the WHAT; the effect is config-driven). */
const PLACEMENT_ZONE_COPY: Record<CaptionPlacementZone, { title: string; blurb: string }> = {
  top: { title: "Top banner", blurb: "punchy, neutral" },
  chyron: { title: "Chyron", blurb: "looks official" },
  subject: { title: "Over the subject", blurb: "covers context" },
};

/** A legible, per-axis-colored cue for the caption's current placement zone. */
export interface PlacementCue {
  zone: CaptionPlacementZone;
  /** Short zone name, e.g. "Chyron". */
  title: string;
  /** Plain-words effect, e.g. "looks official". */
  blurb: string;
  /** Dominant emotion axis this placement moves, or null when neutral. */
  axis: EmotionAxis | null;
  /** Direction of the dominant move ("up" / "down"), or null when neutral. */
  dir: "up" | "down" | null;
  /** Palette color for the axis word (spider palette), or undefined when neutral. */
  color?: string;
}

/**
 * Build the placement cue for a zone from the scenario config. The axis + arrow
 * are DERIVED from the config's `captionPlacement[zone]` contribution (largest
 * magnitude wins), so the cue always tells the truth about what the placement
 * does. Presentation only; never feeds scoring.
 */
export function placementCue(zone: CaptionPlacementZone, config: LandingConfig): PlacementCue {
  const copy = PLACEMENT_ZONE_COPY[zone];
  const spec = config.levers.captionPlacement?.[zone];
  let axis: EmotionAxis | null = null;
  let best = 0;
  if (spec) {
    for (const a of EMOTION_AXES) {
      const v = spec[a];
      if (v !== undefined && Math.abs(v) > Math.abs(best)) {
        best = v;
        axis = a;
      }
    }
  }
  const dir = best > 0 ? "up" : best < 0 ? "down" : null;
  return {
    zone,
    title: copy.title,
    blurb: copy.blurb,
    axis,
    dir,
    color: axis ? EMOTION_AXIS_COLORS[axis] : undefined,
  };
}
